import React, { useState } from 'react'
import { Button, Popconfirm, Tooltip, message } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { withNamespaces } from 'react-i18next'
import { hookService } from 'services/hookService'

const MUTED_COLOR = '#8c8c8c'

const tx = (t, key, options) => t(key, { ...options, interpolation: { escapeValue: false } })

/**
 * "Reenviar" um envio de webhook que falhou, como o Retry do n8n: manda o mesmo conteúdo de novo e a
 * tentativa vira um envio novo na lista. Só aparece em envio que falhou e ainda não foi reenviado
 * (event.canRetry, calculado pelo backend). onRetried(novoEnvio) é chamado no fim, para recarregar a lista.
 */
const RetryHookEventButtonBase = ({ t, event, onRetried, size = 'small' }) => {
  const [loading, setLoading] = useState(false)
  if (!event || !event.canRetry) return null

  const retry = async () => {
    setLoading(true)
    try {
      const { data } = await hookService.retryEvent(event.id)
      const next = data && data.event
      if (next && next.sended) {
        message.success(
          tx(t, 'hooks.retry.sent', { defaultValue: 'Reenviado · {{code}}', code: next.resultCode })
        )
      } else {
        message.warning(
          tx(t, 'hooks.retry.failedAgain', {
            defaultValue: 'A nova tentativa também falhou · {{code}} {{reason}}',
            code: next ? next.resultCode : '',
            reason: (next && next.resultMessage) || '',
          })
        )
      }
      if (onRetried) onRetried(next)
    } catch (err) {
      const data = err && err.response && err.response.data
      message.error((data && data.message) || t('hooks.retry.error', 'Não foi possível reenviar.'))
      // a lista pode ter mudado (outra pessoa reenviou): recarrega para mostrar o estado atual
      if (onRetried) onRetried(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Popconfirm
      title={
        <ConfirmText>
          <b>{t('hooks.retry.confirmTitle', 'Reenviar este envio?')}</b>
          <span>
            {t(
              'hooks.retry.confirmText',
              'O mesmo conteúdo vai de novo para o webhook e a nova tentativa fica registrada na lista. Cada envio só pode ser reenviado uma vez.'
            )}
          </span>
        </ConfirmText>
      }
      okText={t('hooks.retry.button', 'Reenviar')}
      cancelText={t('hooks.retry.cancel', 'Cancelar')}
      onConfirm={retry}
    >
      <Button
        size={size}
        icon={<ReloadOutlined />}
        loading={loading}
        onClick={(e) => e.stopPropagation()}
      >
        {t('hooks.retry.button', 'Reenviar')}
      </Button>
    </Popconfirm>
  )
}

export const RetryHookEventButton = withNamespaces()(RetryHookEventButtonBase)

/**
 * Situação da tentativa: "2ª tentativa · reenviado por fulano", "Reenviado" (já tem tentativa nova)
 * ou "Aguardando resposta".
 */
const RetryInfoBase = ({ t, event }) => {
  if (!event) return null
  const parts = []
  if (event.sending) parts.push(t('hooks.retry.sending', 'Aguardando resposta…'))
  if (event.attempt > 1)
    parts.push(
      event.retryUser
        ? tx(t, 'hooks.retry.attemptBy', {
            defaultValue: '{{n}}ª tentativa · reenviado por {{user}}',
            n: event.attempt,
            user: event.retryUser,
          })
        : tx(t, 'hooks.retry.attempt', { defaultValue: '{{n}}ª tentativa', n: event.attempt })
    )
  if (event.retriedByEventId) parts.push(t('hooks.retry.retried', 'Já reenviado'))
  if (parts.length === 0) return null
  return (
    <Tooltip
      title={
        event.retriedByEventId
          ? t(
              'hooks.retry.retriedHint',
              'Este envio já foi reenviado. Se a nova tentativa também falhar, reenvie a partir dela.'
            )
          : undefined
      }
    >
      <Info>{parts.join(' · ')}</Info>
    </Tooltip>
  )
}

export const RetryInfo = withNamespaces()(RetryInfoBase)

const ConfirmText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 300px;

  span {
    font-size: 12px;
    color: #595959;
  }
`

const Info = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: ${MUTED_COLOR};
`
